import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Settings, Phone, MessageSquare, Save, DollarSign, Percent, Tag } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

// Sadece halı türü adları. Fiyat ve indirim varsayılanları BACKEND'dedir (GET /api/admin/settings
// zaten birleştirilmiş değerleri döndürür); burada ayrı bir varsayılan fiyat tablosu tutulmaz.
const DEFAULT_PRICES = {
  makine: { name: "Makine Halısı" },
  shaggy: { name: "Shaggy Halı" },
  bambu: { name: "Bambu Halı" },
  viskon: { name: "Viskon Halı" },
  akrilik: { name: "Akrilik Halı" },
  kilim: { name: "Kilim" },
  el_dokuma: { name: "El Dokuma" },
  yun: { name: "Yün Halı" },
  isparta: { name: "Isparta Halısı" },
  hasir: { name: "Hasır Halı" },
  hereke: { name: "Hereke Halısı" },
  ladik: { name: "Ladik Halısı" },
  step: { name: "Step Halı" },
  usak: { name: "Uşak Halısı" },
  cin: { name: "Çin Halısı" },
  afgan: { name: "Afgan Halısı" },
  bunyan: { name: "Bünyan Halı" },
  iran: { name: "İran Halısı" },
  ipek: { name: "İpek Halı" },
  nepal: { name: "Nepal Halısı" },
  deri: { name: "Deri Halı" },
  patchwork: { name: "Patchwork" },
};

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    whatsapp_number: "",
    whatsapp_message: "",
    discount_rate: 20,
    carpet_prices: {}
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false); // yükleme başarısızsa kaydetmeyi engelle (DB'yi boş/varsayılanla ezmesin)

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/settings`);
      const fetchedSettings = res.data.settings;
      setSettings({
        ...fetchedSettings,
        carpet_prices: fetchedSettings.carpet_prices || {},
        discount_rate: fetchedSettings.discount_rate ?? 0,
      });
    } catch (error) {
      console.error(error);
      setLoadError(true);
      toast.error("Ayarlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/settings`, settings);
      toast.success("Ayarlar başarıyla kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const updateCarpetPrice = (key, value) => {
    setSettings({
      ...settings,
      carpet_prices: {
        ...settings.carpet_prices,
        [key]: parseFloat(value) || 0
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="py-12 text-center text-slate-600">
        <p className="mb-4">Ayarlar yüklenemedi. Mevcut değerlerin üzerine yazmamak için kaydetme kapatıldı.</p>
        <button type="button" className="underline font-semibold" onClick={() => window.location.reload()}>Tekrar dene</button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-7 h-7 text-orange-500" />
        <h1 className="text-2xl font-bold text-slate-800">Sistem Ayarları</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* WhatsApp Ayarları */}
        <div className="dashboard-card space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Phone className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-slate-800">WhatsApp İletişim</h2>
          </div>

          <div>
            <Label>WhatsApp Numarası *</Label>
            <Input
              value={settings.whatsapp_number}
              onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
              placeholder="902126990301"
              className="mt-1"
            />
            <p className="text-xs text-slate-500 mt-1">
              Format: 90 ile başlayan 12 haneli numara (örn: 902126990301)
            </p>
          </div>

          <div>
            <Label>Varsayılan Mesaj</Label>
            <Textarea
              value={settings.whatsapp_message}
              onChange={(e) => setSettings({ ...settings, whatsapp_message: e.target.value })}
              placeholder="Merhaba, halı yıkama hizmeti hakkında bilgi almak istiyorum."
              rows={4}
              className="mt-1"
            />
          </div>

          {/* Önizleme */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4 text-green-600" />
              <span className="text-sm font-semibold text-green-800">Önizleme</span>
            </div>
            <p className="text-sm text-green-700">
              WhatsApp'ta açılacak numara: <strong>{settings.whatsapp_number || "Belirtilmedi"}</strong>
            </p>
            <p className="text-sm text-green-700 mt-1">
              Mesaj: "{settings.whatsapp_message || "Belirtilmedi"}"
            </p>
          </div>
        </div>

        {/* İndirim Ayarları */}
        <div className="dashboard-card space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Percent className="w-5 h-5 text-orange-600" />
            <h2 className="text-lg font-semibold text-slate-800">İndirim Ayarları</h2>
          </div>

          <div>
            <Label>Yeni Üye İndirim Oranı (%)</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                min="0"
                max="100"
                value={settings.discount_rate}
                onChange={(e) => setSettings({ ...settings, discount_rate: parseFloat(e.target.value) || 0 })}
                className="w-32"
              />
              <span className="text-slate-600">%</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Tüm siparişlerde uygulanacak indirim oranı
            </p>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Tag className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-semibold text-orange-800">Örnek Hesaplama</span>
            </div>
            <p className="text-sm text-orange-700">
              100₺ liste fiyatlı ürün için: <strong>{(100 * (1 - settings.discount_rate / 100)).toFixed(0)}₺</strong> ödenecek
            </p>
            <p className="text-sm text-orange-700">
              İndirim tutarı: <strong>{settings.discount_rate}₺</strong>
            </p>
          </div>
        </div>

        {/* Halı Fiyatları */}
        <div className="dashboard-card lg:col-span-2 space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-slate-800">Halı Fiyatları (₺/m²)</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(DEFAULT_PRICES).map(([key, { name }]) => (
              <div key={key} className="bg-stone-50 rounded-lg p-3">
                <Label className="text-xs text-slate-600">{name}</Label>
                <div className="flex items-center gap-1 mt-1">
                  <Input
                    type="number"
                    min="0"
                    value={settings.carpet_prices?.[key] ?? ""}
                    onChange={(e) => updateCarpetPrice(key, e.target.value)}
                    className="h-8 text-sm"
                  />
                  <span className="text-xs text-slate-500">₺/m²</span>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">💡 Bilgi</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Fiyat değişiklikleri kaydedildikten sonra yeni siparişlerde geçerli olur</li>
              <li>• Mevcut siparişlerin fiyatları değişmez</li>
              <li>• Firma anlaşma fiyatları bu fiyatların %60-70'i civarındadır</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Kaydet Butonu */}
      <div className="mt-6">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 w-full md:w-auto px-8"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Kaydediliyor..." : "Tüm Ayarları Kaydet"}
        </Button>
      </div>
    </div>
  );
};

export default AdminSettings;
